import React, { FunctionComponent } from 'react';
import {
  ButtonBase,
  Chip,
  ChipProps,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import moment from 'moment';

import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../../../components/V2/Table/type1/TableStyles';
import {
  IMeetingRequest,
  MEETING_REQUEST_REQUESTED_BY_ROLES,
  MEETING_REQUEST_STATUSES,
  MEETING_REQUEST_TYPES,
} from '../../../../../../store/V2/MeetingRequestsSlice';
import { IUser } from '../../../../../../store/UserSlice';
import { checkUserPermission } from '../../../../../../helper';
import { getAuthState } from '../../../../../../store/AuthSlice';
import { useAppSelector } from '../../../../../../hooks';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../store/V2/RolesSlice';

interface RescheduledOrCancelledTableRowProps {
  requestData: IMeetingRequest;
  onView: () => void;
  onAppeal: () => void;
  onStudentsClick: () => void;
}

const actionButtonStyles: SxProps = {
  borderRadius: '8px',
  padding: '10px 12px 10px 12px',
  fontWeight: 600,
  fontSize: '14px',
  maxHeight: '36px',
  backgroundColor: '#006C9C',
  color: '#F8FAFC',
  width: 'fit-content',
};

const RescheduledOrCancelledTableRow: FunctionComponent<RescheduledOrCancelledTableRowProps> = ({
  requestData,
  onView,
  onAppeal,
  onStudentsClick,
}) => {
  // Store
  const authUserState = useAppSelector(getAuthState);

  const authUser = authUserState.user;
  const reason = requestData.reason || '-';
  const requestedAt = moment(requestData.createdAt);
  const scheduledAt = requestData.start ? moment(requestData.start) : undefined;
  const newScheduledAt = requestData.newStart
    ? moment(requestData.newStart)
    : null;

  const rescheduleTypeBadgeProps: ChipProps = {
    size: 'small',
    label: requestData.type.toLocaleLowerCase(),
    color: 'warning',
    sx: {
      textTransform: 'capitalize',
      width: '100%',
      borderRadius: '4px',
      fontSize: '14px',
      fontWeight: 600,
    },
  };

  const rescheduleStatusBadgeProps: ChipProps = {
    size: 'small',
    label: requestData.status.toLocaleLowerCase(),
    color: 'warning',
    sx: {
      textTransform: 'capitalize',
      width: '100%',
      borderRadius: '4px',
      fontSize: '14px',
      fontWeight: 600,
    },
  };

  let studentName = '';
  let tutorPenalty = '-';
  let tutorPenaltyAmount = '-';
  let studentPenalty = '-';
  let studentPenaltyAmount = '-';

  if (
    requestData.penalty &&
    requestData.requestedByRole !== MEETING_REQUEST_REQUESTED_BY_ROLES.STUDENT
  ) {
    tutorPenalty = `${parseFloat(requestData.penalty.toFixed(2))}%`;

    if (requestData.penaltyAmount && requestData.tutorAmountCurrency) {
      tutorPenaltyAmount = `${
        requestData.tutorAmountCurrency
      } ${requestData.penaltyAmount.toFixed(2)}`;
    }
  }

  if (
    requestData.penalty &&
    requestData.requestedByRole === MEETING_REQUEST_REQUESTED_BY_ROLES.STUDENT
  ) {
    studentPenalty = `${parseFloat(requestData.penalty.toFixed(2))}%`;

    if (requestData.penaltyAmount && requestData.tutorAmountCurrency) {
      studentPenaltyAmount = `${
        requestData.tutorAmountCurrency
      } ${requestData.penaltyAmount.toFixed(2)}`;
    }
  }

  if (requestData.student) {
    const _student = requestData.student as IUser;

    studentName = _student.firstname;

    if (_student.lastname) {
      studentName += ` ${_student.lastname}`;
    }
  }

  switch (requestData.type) {
    case MEETING_REQUEST_TYPES.CANCEL:
      rescheduleTypeBadgeProps.color = 'error';
      break;

    case MEETING_REQUEST_TYPES.DELETE:
      rescheduleTypeBadgeProps.color = 'error';
      break;

    default:
      break;
  }

  switch (requestData.status) {
    case MEETING_REQUEST_STATUSES.APPROVED:
      rescheduleStatusBadgeProps.color = 'success';
      break;

    case MEETING_REQUEST_STATUSES.REJECTED:
      rescheduleStatusBadgeProps.color = 'error';
      break;

    default:
      break;
  }

  const renderRequestAppealContent = () => {
    const isFeatureStarted =
      moment(
        process.env
          .REACT_APP_TUTOPIYA_PENALTY_WAIVE_OFF_REQUEST_FEATURE_START_DATE,
        'YYYY-MM-DD'
      ) < moment();

    const isExpired = moment(requestData.start).add(1, 'month') < moment();
    const isDisabled = requestData.isPenaltyAppealRequestReceived;

    const buttonStyles: SxProps = {
      textDecoration: 'underline',
      width: 'fit-content',
    };

    if (isDisabled) {
      buttonStyles.color = 'rgba(0, 0, 0, 0.26)';
    }

    if (
      checkUserPermission(authUser, {
        penaltyWaivers: [USER_PERMISSION_INDIVIDUALS.PENALTY_APPEAL_REQUEST],
      })
    ) {
      if (
        requestData.penaltyAmount &&
        requestData.requestedByRole !==
          MEETING_REQUEST_REQUESTED_BY_ROLES.STUDENT &&
        isFeatureStarted &&
        !isExpired
      ) {
        return (
          <ButtonBase
            disabled={isDisabled}
            sx={buttonStyles}
            onClick={onAppeal}
          >
            {requestData.isPenaltyAppealRequestReceived
              ? 'Request sent'
              : 'Request an appeal'}
          </ButtonBase>
        );
      }
    }

    return null;
  };

  return (
    <StyledTableRow hover key={requestData._id}>
      {/* <StyledTableCell padding="checkbox">
        <Checkbox checked={selected} onClick={onSelectRow} />
      </StyledTableCell> */}
      <StyledTableCell>
        {studentName ? (
          <ButtonBase
            sx={{ textDecoration: 'underline', textAlign: 'unset' }}
            onClick={onStudentsClick}
          >
            {studentName}
          </ButtonBase>
        ) : (
          <Typography
            fontSize="14px"
            fontWeight={500}
            color="#1E293B"
            textTransform="capitalize"
          >
            -
          </Typography>
        )}
      </StyledTableCell>
      <StyledTableCell>
        <Stack>
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            {requestedAt.format('ddd MMM DD YYYY')}
          </Typography>
          <Typography fontSize="12px" color="#94A3B8">
            {requestedAt.format('hh:mm A')}
          </Typography>
        </Stack>
      </StyledTableCell>
      <StyledTableCell>
        <Typography fontSize="14px" fontWeight={500} color="#1E293B">
          {scheduledAt ? scheduledAt.format('ddd MMM DD YYYY') : '-'}
        </Typography>
        {scheduledAt ? (
          <Typography fontSize="12px" color="#94A3B8">
            {scheduledAt.format('hh:mm A')}
          </Typography>
        ) : null}
      </StyledTableCell>
      <StyledTableCell>
        <Typography fontSize="14px" fontWeight={500} color="#1E293B">
          {newScheduledAt ? newScheduledAt.format('ddd MMM DD YYYY') : '-'}
        </Typography>
        {newScheduledAt ? (
          <Typography fontSize="12px" color="#94A3B8">
            {newScheduledAt.format('hh:mm A')}
          </Typography>
        ) : null}
      </StyledTableCell>
      <StyledTableCell>
        <Chip {...rescheduleTypeBadgeProps} />
      </StyledTableCell>
      <StyledTableCell>
        <Chip {...rescheduleStatusBadgeProps} />
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          textTransform="capitalize"
        >
          {tutorPenalty}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Stack>
          <Typography
            fontSize="14px"
            fontWeight={500}
            color="#1E293B"
            textTransform="capitalize"
          >
            {tutorPenaltyAmount}
          </Typography>
          {renderRequestAppealContent()}
        </Stack>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          textTransform="capitalize"
        >
          {studentPenalty}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          textTransform="capitalize"
        >
          {studentPenaltyAmount}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {reason}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Stack alignItems="flex-end">
          <ButtonBase sx={{ ...actionButtonStyles }} onClick={onView}>
            View
          </ButtonBase>
        </Stack>
      </StyledTableCell>
    </StyledTableRow>
  );
};

export default RescheduledOrCancelledTableRow;
